import { faUserGraduate } from "@fortawesome/pro-regular-svg-icons/faUserGraduate";
import { faUsersClass } from "@fortawesome/pro-regular-svg-icons/faUsersClass";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography } from "@mui/material";
import blue from '@mui/material/colors/blue';
import green from '@mui/material/colors/green';
import grey from '@mui/material/colors/grey';
import red from '@mui/material/colors/red';
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import { SpinnerIndicator } from "components/CallServer";
import DashDoughnutBox from "components/Dashboard/DashDoughnutBox";
import { JoinComponents } from "components/JoinComponents";
import MuiBox from "components/MuiExtras/MuiBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { TextPluralization } from "components/TextPluralization";
import { DashboardCourseAdminAnalysisModel } from "models/ApiModels";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    loading: boolean;
    data: DashboardCourseAdminAnalysisModel;
}

interface IGraphProps {
    courseId: string;
}

type GraphProps = IGraphProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class CourseAdminAnalysis extends React.PureComponent<GraphProps, IGraphState> {

    constructor(props: GraphProps) {
        super(props);

        this.state = {
            data: null,
            loading: true
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.CourseDashboard.AdminAnalysis." + r);
    }

    ///
    private loadData() {

        this.setState({ loading: true });

        api.getDashboardCourseAdminAnalysis(this.props.courseId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private renderHeader() {
        const { loading } = this.state;
        return (
            <MuiContainer>
                <MuiBox variant="notice">
                    <Grid container justifyContent="space-between" alignItems="center" >
                        <Grid item>

                            <MuiColor variant="mutedDark">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faUsersClass} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.enrollments}</Typography>
                                        }
                                        <span>
                                            <TextPluralization
                                                count={loading ? 0 : this.state.data.enrollments}
                                                resource="Views.Dashboards.CourseDashboard.AdminAnalysis.HeaderTitle"
                                            />
                                        </span>
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>

                        <Grid item>
                            <MuiColor variant="info">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faUsersClass} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.inProgress}</Typography>
                                        }
                                        <span>{this.res("InProgress")}</span>
                                    </Grid>
                                </Grid>
                            </MuiColor>
                        </Grid>

                        <Grid item>

                            <MuiColor variant="success">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faUserGraduate} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.completed}</Typography>
                                        }
                                        <span>
                                            <TextPluralization
                                                count={loading ? 0 : this.state.data.completed}
                                                resource="Views.Dashboards.CourseDashboard.AdminAnalysis.CompleteCount"
                                            />
                                        </span>
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>
                    </Grid>
                </MuiBox >
            </MuiContainer>
        );
    }

    ///
    private renderGauges() {
        const { loading, data } = this.state;
        return (
            <MuiContainer>
                <Grid container justifyContent="space-between" alignItems="center" >
                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="1"
                                    active={false}
                                    title={<span>{this.res("StartedTitle")}<br /><br /></span>}
                                    titlePosition="top"
                                    value={(data.enrollments > 0) ? (100.0 * (data.enrollments - data.notInitiate) / data.enrollments) : 0}
                                    color="success"
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="2"
                                    active={false}
                                    title={<JoinComponents components={this.res("DoesNotProgressTitle").split('\n')} separator={<br />} />}
                                    titlePosition="top"
                                    value={(data.enrollments > 0) ? (100.0 * data.noProgress30 / data.enrollments) : 0}
                                    color="error"
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="3"
                                    active={false}
                                    title={<JoinComponents components={this.res("CompletedTitle").split('\n')} separator={<br />} />}
                                    titlePosition="top"
                                    value={(data.completed > 0) ? (100.0 * data.completeInOneWeek / data.completed) : 0}
                                    color="success"
                                />
                        }
                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderDistribution() {

        if (this.state.loading) {
            return <SpinnerIndicator />;
        }

        const { data } = this.state;
        const ds = [data.notInitiate, data.noProgress30, data.inProgress, data.completed];
        //const total = ds.reduce((p, c) => p + c);

        return (
            <MuiContainer>

                <div className={GlobalClassKeys.textCenter}>
                    <Typography variant="h5"><MuiColor variant="mutedDark">{this.res("DistributionHeaderTitle")}</MuiColor></Typography>
                </div>

                <div style={{ height: 150 }} >
                    <Bar
                        data={
                            {
                                labels: [this.res("DistributionLabel")],
                                datasets: [
                                    {
                                        label: this.res("NotStartedLabel"),
                                        backgroundColor: grey[300],
                                        data: [ds[0] / data.enrollments * 100.0]
                                    },
                                    {
                                        label: this.res("DoesNotProgressLabel"),
                                        backgroundColor: red[500],
                                        data: [ds[1] / data.enrollments * 100.0]
                                    },
                                    {
                                        label: this.res("ProgressLabel"),
                                        backgroundColor: blue[500],
                                        data: [ds[2] / data.enrollments * 100.0]
                                    },
                                    {
                                        label: this.res("CompletedLabel"),
                                        backgroundColor: green[500],
                                        data: [ds[3] / data.enrollments * 100.0]
                                    }
                                ]
                            }
                        }
                        options={
                            {
                                indexAxis: 'y',
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'bottom',
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (item: any) {
                                                return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                            },
                                        }
                                    }
                                },

                                scales: {
                                    xAxes: {
                                        display: true,
                                        stacked: true,
                                        grid: {
                                            display: true
                                        },
                                        title: {
                                            display: true,
                                        },
                                        min: 0,
                                        max: 100,
                                        ticks: {
                                            callback: function (value: number) {
                                                return value.toFixed(0) + '%'; // convert it to percentage
                                            },
                                        }
                                    },
                                    y: {
                                        display: false,
                                        stacked: true,
                                        title: {
                                            display: true,
                                        }
                                    }
                                },
                            }
                        }
                    />
                </div>
            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { loading, data } = this.state;

        return (
            <>
                {
                    !loading && data == null
                        ? <AppMessageBox resource="LoadGraphDataFailed" />
                        : (
                            <div>
                                {this.renderHeader()}
                                {this.renderGauges()}
                                {this.renderDistribution()}
                            </div>
                        )
                }
            </>
        );
    }
}

export default withTranslation()(CourseAdminAnalysis);